import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { usePlatformPrefix } from '@/lib/platform-scope';

export const CONVERSATIONS_ROUTES = { LIST: '/atendimento-whatsapp' } as const;

/** `/api/v1/conversations` no salao; `/api/v1/saas/venda/conversations` reusada pelo saas admin. */
function useEndpoint(): string {
  return `/api/v1${usePlatformPrefix()}/conversations`;
}

export type ConversationStatus = 'OPEN' | 'PENDING' | 'CLOSED';
export type MessageStatus = 'QUEUED' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

export interface Conversation {
  id: string;
  unitId: string;
  unitName: string;
  customerId: string | null;
  contactPhone: string;
  displayName: string;
  customerImage: string | null;
  status: ConversationStatus;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unreadCount: number;
  assignedToId: string | null;
  assignedToName: string | null;
  /** A conversa esta com uma pessoa, e nao com o assistente. */
  handledByHuman: boolean;
  withBot: boolean;
  canSendFreeform: boolean;
}

export interface ChatMessage {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  origin: 'AUTOMATIC' | 'MANUAL';
  body: string;
  status: MessageStatus;
  errorCode: number | null;
  errorMessage: string | null;
  senderName: string | null;
  createdAt: string;
}

const KEYS = {
  list: (prefix: string, status: string, term: string) =>
    ['conversations', prefix, status, term] as const,
  all: (prefix: string) => ['conversations', prefix] as const,
  detail: (prefix: string, id: string) => ['conversation', prefix, id] as const,
  messages: (prefix: string, id: string) => ['conversation-messages', prefix, id] as const,
};

export function useConversations(params: { status?: ConversationStatus; q?: string }) {
  const endpoint = useEndpoint();
  const prefix = usePlatformPrefix();
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.q) search.set('q', params.q);

  return useQuery({
    queryKey: KEYS.list(prefix, params.status ?? '', params.q ?? ''),
    queryFn: () => apiClient.get<{ data: Conversation[] }>(`${endpoint}?${search}`),
    refetchInterval: 10_000,
  });
}

export function useConversation(id: string | null) {
  const endpoint = useEndpoint();
  const prefix = usePlatformPrefix();

  return useQuery({
    queryKey: KEYS.detail(prefix, id ?? ''),
    queryFn: () => apiClient.get<Conversation>(`${endpoint}/${id}`),
    enabled: Boolean(id),
  });
}

export function useConversationMessages(id: string | null) {
  const endpoint = useEndpoint();
  const prefix = usePlatformPrefix();

  return useQuery({
    queryKey: KEYS.messages(prefix, id ?? ''),
    queryFn: () => apiClient.get<{ data: ChatMessage[] }>(`${endpoint}/${id}/messages`),
    enabled: Boolean(id),
    refetchInterval: 5_000,
  });
}

export function useSendMessage(id: string) {
  const endpoint = useEndpoint();
  const prefix = usePlatformPrefix();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: string) =>
      apiClient.post<ChatMessage>(`${endpoint}/${id}/messages`, { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.messages(prefix, id) });
      queryClient.invalidateQueries({ queryKey: KEYS.all(prefix) });
    },
  });
}

export function useMarkRead() {
  const endpoint = useEndpoint();
  const prefix = usePlatformPrefix();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.post<Conversation>(`${endpoint}/${id}/read`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all(prefix) }),
  });
}

export function useUpdateConversation(id: string) {
  const endpoint = useEndpoint();
  const prefix = usePlatformPrefix();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      status?: ConversationStatus;
      assignToMe?: boolean;
      customerId?: string;
    }) => apiClient.patch<Conversation>(`${endpoint}/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.detail(prefix, id) });
      queryClient.invalidateQueries({ queryKey: KEYS.all(prefix) });
    },
  });
}
